import { Injectable, inject } from '@angular/core';

import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { RendererService } from '../../services/renderer/renderer.service';

@Injectable()
export class DigitKeyboardInputGlobalState {
    private rendererService = inject(RendererService);
    private windowRefService = inject(WindowRefService);
    private pubSubService = inject(PubSubService);

    // cache elements to not get them from DOM on every directive instance because they are the same
    private elementsCache: Record<
        string,
        {
            mainElement: HTMLElement | null;
            keyboardElement: HTMLElement | null;
            qsElement: HTMLElement | null;
        } | null
    > = {};

    // cache bsSelectionsElement instead of getting it from cache
    private bsSelectionsElement: HTMLElement | null = null;

    constructor() {
        (window as any).getThis = () => console.log({ scope: this });
    }

    getBsSelectionsElement(): HTMLElement | null {
        if (this.bsSelectionsElement) {
            return this.bsSelectionsElement;
        }
        const el = this.windowRefService.document.querySelector('#bs-selections-wrapper') as HTMLElement | null;
        this.bsSelectionsElement = el;
        return el;
    }

    getElements(componentId: string) {
        if (this.elementsCache[componentId]) {
            return this.elementsCache[componentId];
        }

        const mainElement = this.windowRefService.document.getElementById(componentId);
        const keyboardElement = mainElement?.querySelector('#dk-keyboard') as HTMLElement;
        const qsElement = mainElement?.querySelector('.quickbet-quick-stake') as HTMLElement;

        this.elementsCache[componentId] = {
            mainElement,
            keyboardElement,
            qsElement,
        };

        return this.elementsCache[componentId];
    }

    /**
     * Hide keyboard only once for componentId
     */
    hideKeyboard(componentId: string): void {
        this.pubSubService.publish(this.pubSubService.API.DIGIT_KEYBOARD_HIDDEN, componentId);
    }

    private _lastElementCached: HTMLElement | null = null;
    private _inputElCached: HTMLInputElement[] = [];

    /**
     * Clears active class, restores placeholders
     */
    clearActiveElement(data: ClearActiveElementData): void {
        const { element, depositPlaceholder, qdAmount, dkActiveInputClass, freeBetUuidAttr, qdCvvId, isTotePool } = data;

        if (element !== this._lastElementCached) {
            // if we are clearing the inputs for a different parent el, we want to cleanup the last cached inputs
            this._inputElCached = [];
        }

        // eslint-disable-next-line sonarjs/no-duplicate-string

        if (this._inputElCached.length === 0) {
            this._inputElCached = [...element.querySelectorAll<HTMLInputElement>('input[type=text], .custom-input-container')];
            this._lastElementCached = element;
        }

        for (let inputElement of this._inputElCached) {
            this.rendererService.renderer.removeClass(inputElement, dkActiveInputClass);

            //removes the previous subscriber to avoid adding freebet selection to the previous stake
            if (inputElement && inputElement.attributes?.[freeBetUuidAttr as any]) {
                this.pubSubService.unsubscribe(inputElement.attributes[freeBetUuidAttr as any].value);
                this.rendererService.renderer.removeAttribute(inputElement, freeBetUuidAttr);
            }
            // CVV field should be without placeholder
            if (inputElement && inputElement.id !== qdCvvId && inputElement.id !== qdAmount && !isTotePool) {
                this.rendererService.renderer.setAttribute(inputElement, 'placeholder', 'Stake');
            } else if (inputElement && inputElement.id === qdAmount && depositPlaceholder) {
                this.rendererService.renderer.setAttribute(inputElement, 'placeholder', depositPlaceholder);
            }
        }
    }

    // used to cache the inputs and not get them all the time using querySelectorAll
    private _bsSelectionElementInputs: HTMLInputElement[] = []; // caching
    /**
     * Find and format all inputs value
     */
    allInputsValueToFixed(): void {
        const bsSelectionElement = this.getBsSelectionsElement();
        if (!bsSelectionElement) {
            return;
        }

        if (this._bsSelectionElementInputs.length === 0) {
            this._bsSelectionElementInputs = [...bsSelectionElement.querySelectorAll<HTMLInputElement>('input[type=text], .custom-input-container')];
        }

        for (let inputElement of this._bsSelectionElementInputs) {
            inputElement &&
                inputElement.value &&
                this.rendererService.renderer.setAttribute(inputElement, 'value', Number(inputElement.value).toFixed(2));
        }
    }

    resetById(componentId: string) {
        this.elementsCache[componentId] = null;
        this.bsSelectionsElement = null;
        this._bsSelectionElementInputs = [];
    }
}

interface ClearActiveElementData {
    element: HTMLElement;
    dkActiveInputClass: string;
    freeBetUuidAttr: string;
    depositPlaceholder: string;
    qdAmount: string;
    qdCvvId: string;
    isTotePool: boolean;
}
